import { useMemo } from "react"
import { Avatar, Box, Chip, Typography, useTheme } from "@mui/material"
import clsx from "clsx"
import { FeatureListItem } from "components/domains/features"
import { ErrorRoundedIcon, ShopIcon } from "../../icons"
import { useTranslation } from "providers/i18n"
import { prepareSearchResultSecondaryText } from "utils/search"
import { getIcon } from "utils/icon"
import { getFeatureDetail } from "utils/feature"
import { ComingSoonChip, NewShopChip } from "components/chips"

export const SearchListItem = ({
  data,
  primaryTypographyProps = {},
  secondaryTypographyProps = {},
  endAdornmentSlotClasses = {},
  endAdornmentSlots = {},
  isComingSoon = false,
  isNewShop = false,
  ...props
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const {
    categoryText,
    floorText,
    zoneText,
    boothNameText,
    groupText,
    avatarProps,
    venueRef,
    isAd = false,
    name,
    hasMultipleLocations,
    locationDescriptionText,
    maintenance,
  } = data
  const theme = useTheme()

  const { venueName, hasLocation } = useMemo(
    () => getFeatureDetail(data),
    [data]
  )

  const { styleOverrides = {}, defaultProps = {} } =
    theme.components[`VuiSearchResultItem`] || {}
  const { chip, primaryWrapper, maintenanceIcon, comingSoonChip, newShopChip } =
    styleOverrides

  const primaryText = t(name)
  const venueText = t(venueName ?? "")

  const defaultIconProps = {
    imageIconProps: {
      solidColorFilterId: "diningCard_cover_filter",
    },
  }

  //Prepare secondary text
  const { secondaryTextKeys, imageIconProps = defaultIconProps } = defaultProps

  const secondaryText = useMemo(
    () =>
      hasMultipleLocations
        ? "Multiple Locations"
        : prepareSearchResultSecondaryText(
            categoryText,
            boothNameText,
            hasLocation ? floorText : null,
            zoneText,
            groupText,
            locationDescriptionText,
            secondaryTextKeys,
            venueText
          ),
    [
      categoryText,
      boothNameText,
      floorText,
      zoneText,
      groupText,
      locationDescriptionText,
      secondaryTextKeys,
      hasMultipleLocations,
      venueText,
      hasLocation,
    ]
  )

  return (
    <FeatureListItem
      divider
      disableTypography
      className={venueRef}
      startAdornment={
        <>
          {isAd && (
            <Chip
              variant="contained"
              label={t("sponsored.search_result")}
              sx={chip}
            />
          )}
          {isComingSoon && !isAd && (
            <ComingSoonChip sx={comingSoonChip} label={t("Coming soon")} />
          )}
          {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
        </>
      }
      primary={
        <Box sx={primaryWrapper}>
          <Typography
            variant="h6"
            className="MuiListItemText-primary"
            {...primaryTypographyProps}
          >
            {maintenance?.enable ? (
              <ErrorRoundedIcon
                color={maintenance?.hasTempLocation ? "warning" : "error"}
                sx={maintenanceIcon}
              />
            ) : null}
            {primaryText}
          </Typography>
        </Box>
      }
      secondary={
        <Typography
          variant="subtitle1"
          className="MuiListItemText-secondary"
          {...secondaryTypographyProps}
        >
          {secondaryText}
        </Typography>
      }
      avatar={
        <Avatar
          src={avatarProps.src}
          variant="logo"
          className={clsx("occupant-logo-avatar", {
            "no-logo": !avatarProps.src,
          })}
        >
          {/* if there is no avatar, will fall back to categoryIconPath > ShopIcon */}
          {getIcon(avatarProps.icon, null, imageIconProps) || <ShopIcon />}
        </Avatar>
      }
      endAdornmentSlotClasses={endAdornmentSlotClasses}
      endAdornmentSlots={!hasMultipleLocations && endAdornmentSlots}
      {...props}
    />
  )
}
